import { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';
//constants
import { pagesEnum, timing } from 'constants/ui.constants';
// @mui
import { Stack } from '@mui/system';
// components
import { Page } from 'theme/elements/Page';
import { CenteredNarrowContent } from 'theme/elements/Content';
import CancelDialog from 'components/CancelDialog';
import Stepping from 'theme/elements/Stepper';
// sections
import CreateHeader from 'sections/convert/upsert/CreateHeader';
// reducers
import { useAppSelector, useAppDispatch } from 'hooks/redux.hooks';
import {
	toggleUpserting,
	getSteps,
	getActiveStep,
	intializeConvert,
	setActiveStep,
} from 'state/reducers/convert.reducers';
import {
	cancelDialogProps,
	getHistory,
	getMobile,
	getUi,
	ifPageChange,
	setCheckBeforeNavigate,
	togglePage,
} from 'state/reducers/ui.reducers';
import MobileNavbar from 'sections/layout/MobileNavbar';
import { Convert, convertSteps } from 'constants/convert.constants';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { FormHeader1, HeaderPage1, getUrlBack } from 'theme/elements/Headers';
import { translatedCancelPopup } from 'utils';
import { getConvertById } from 'services/convert.services';
import { FormsStack } from 'theme/elements/Forms';
import { InfoCard } from 'theme/elements/Cards';
import { Paragraph1, Paragraph3 } from 'theme/elements/Paragraphs';
import { format, isValid } from 'date-fns';
import { getBalanceActivityById } from 'services/activity.services';
import { Button, Typography } from '@mui/material';
import { FormButton } from 'theme/elements/Buttons';
import { getFile } from 'services/global.services';
import { StatusType, BalanceDepositType } from 'constants/balance.constants';
import { getFormattedNumber } from 'utils';

export default function ToppingProfile() {
	const navigate = useNavigate();
	const t = useAppSelector(selectTranslations);
	const location = useLocation();
	const dispatch = useAppDispatch();
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	//get location
	const paths = location.pathname.split('/');
	const lastPath = paths[paths.length - 1];
	const steps = useAppSelector(getSteps);
	const isMobile = useAppSelector(getMobile);
	const activeStep = useAppSelector(getActiveStep);
	const isLeavingPage = useAppSelector(ifPageChange);
	const ui = useAppSelector(getUi);
	const history = useAppSelector(getHistory);
	const urlback = getUrlBack(location.pathname, history[history.length - 1]);
	const stepProps = {
		handleCancel: () => setOpenCancelDialog(true),
	};
	const dialogProps = useAppSelector(cancelDialogProps);
	const dialogPropsTranslated = useMemo(() => translatedCancelPopup(dialogProps), [dialogProps]);
	const [toppingResult, setToppingResult] = useState<(Record<string, any> & Convert) | undefined>();
	const prefixFields = 'page.convert.create.step.3.';
	const prefixHeaderFields = 'page.convert.create.step.1.';

	const contact = useMemo(() => {
		if (_.has(toppingResult, 'balanceActivity.createdBy')) {
			return (
				_.get(toppingResult, 'balanceActivity.createdBy.firstName') +
				' ' +
				_.get(toppingResult, 'balanceActivity.createdBy.lastName')
			);
		}
		return undefined;
	}, [toppingResult]);

	const clientBankAccount = useMemo(() => {
		if (toppingResult && _.has(toppingResult, 'companyBankAccounts')) {
			return _.find(toppingResult.companyBankAccounts, {
				id: _.get(toppingResult, 'balanceActivity.bankAccountId'),
			});
		}
		return undefined;
	}, [toppingResult]);

	const altsulerBankAccount = useMemo(() => {
		if (toppingResult && _.has(toppingResult, 'altshulerAccounts')) {
			return _.find(toppingResult.altshulerAccounts, {
				id: _.get(toppingResult, 'balanceActivity.altshulerAccountId'),
			});
		}
		return undefined;
	}, [toppingResult]);

	const depositCardData: any = useMemo(() => {
		const balanceActivity = toppingResult?.balanceActivity;
		if (!balanceActivity) return undefined;

		const isDigital = balanceActivity.depositType === BalanceDepositType.Digital;

		const status = t(`page.activity.table.status.${_.snakeCase(balanceActivity.status)}.text`);

		const header = isDigital
			? t('page.balance.add.digital.reference.deposit_details.title')
			: t('page.balance.add.header.title');

		const createdAt = balanceActivity.createdAt;
		const formattedCreatedAt = isValid(new Date(createdAt)) ? format(new Date(createdAt), timing.DATE) : createdAt;

		const bankApprovalDate =
			isDigital && balanceActivity.status !== StatusType.Pending && balanceActivity.feezbackUpdatedAt
				? format(new Date(balanceActivity.feezbackUpdatedAt), timing.FULLDATEHEBREW)
				: isDigital
				? 'N/A'
				: undefined;

		const amount = _.get(balanceActivity, 'amount', '');
		const currencyAmount = `${_.get(balanceActivity, 'currency.symbol', '')}${getFormattedNumber(amount)}`;

		const hasReferenceFile =
			balanceActivity.depositType === BalanceDepositType.Manual &&
			_.has(balanceActivity, 'file') &&
			_.has(balanceActivity, 'companyId');

		const fileKey = _.get(balanceActivity, 'file.key', '');
		const companyId = _.toNumber(_.get(balanceActivity, 'companyId'));

		return {
			header,
			status,
			formattedCreatedAt,
			bankApprovalDate,
			currencyAmount,
			hasReferenceFile,
			fileKey,
			companyId,
		};
	}, [t, toppingResult]);

	useEffect(() => {
		dispatch(getBalanceActivityById(lastPath)).then((res) => {
			if (res) {
				setToppingResult({ ...res });
			}
		});
	}, [lastPath]);

	return (
		<Fragment>
			<MobileNavbar
				handleBack={() => dispatch(togglePage(urlback))}
				handleCancel={() => setOpenCancelDialog(true)}
			/>
			<Page>
				<Stack spacing={{ sm: 5, xs: 1 }}>
					<CenteredNarrowContent>
						{toppingResult && (
							<FormsStack>
								<InfoCard>
									<FormHeader1 title={depositCardData.header} />

									<Paragraph3
										title={t(prefixFields + 'created_date.text')}
										text={depositCardData.formattedCreatedAt}
									/>

									{depositCardData.bankApprovalDate && (
										<Paragraph3
											title={t(
												'page.balance.add.digital.reference.deposit_details.bank_approval_date'
											)}
											text={depositCardData.bankApprovalDate}
										/>
									)}

									<Paragraph3 title={t(prefixFields + 'created_by.text')} text={contact} />

									<Paragraph3
										title={t('page.balance.add.digital.reference.deposit_details.deposit_status')}
										text={depositCardData.status}
									/>

									<Paragraph3
										title={t('page.balance.add.step.2.currency_approval.header.title')}
										text={depositCardData.currencyAmount}
									/>

									{depositCardData.hasReferenceFile && (
										<FormButton autoheight="true" autowidth="true">
											<Button
												variant="outlined"
												onClick={() => {
													dispatch(
														getFile(depositCardData.fileKey, depositCardData.companyId)
													);
												}}
											>
												{t(
													'page.balance.add.step.2.currency_approval.reference_doc.header.title'
												)}
											</Button>
										</FormButton>
									)}
								</InfoCard>

								<InfoCard>
									<FormHeader1 title={t(`page.balance.add.step.1.title`)} />
									<Stack>
										<Typography>{_.get(clientBankAccount, 'accountOwnerName')}</Typography>
										<Typography>{_.get(clientBankAccount, 'bankName')}</Typography>
										<Typography>{_.get(clientBankAccount, 'bankBranch')}</Typography>
										<Typography>{_.get(clientBankAccount, 'accountNumber')}</Typography>
									</Stack>
								</InfoCard>

								<InfoCard>
									<FormHeader1 title={t(`page.balance.add.step.3.title`)} />
									<Stack>
										<Typography>{_.get(altsulerBankAccount, 'bank.bankName')}</Typography>
										<Typography>{_.get(altsulerBankAccount, 'bank.city')}</Typography>
										<Typography>{_.get(altsulerBankAccount, 'bankBranch')}</Typography>
										<Typography>{_.get(altsulerBankAccount, 'accountNumber')}</Typography>
									</Stack>
								</InfoCard>
							</FormsStack>
						)}
					</CenteredNarrowContent>
				</Stack>
			</Page>
		</Fragment>
	);
}
